import {
  LineChart as MuiLineChart,
  type LineChartProps as MuiLineChartProps,
} from "@mui/x-charts/LineChart";
import { useTheme } from "@mui/material/styles";
import { fontSize, lineHeight } from "../../theme/tokens";
import { useChartColors } from "./useChartColors";

import { CustomTooltip } from "./CustomTooltip";

export interface LineChartProps extends Omit<MuiLineChartProps, "series"> {
  series?: MuiLineChartProps["series"];
}

export function LineChart({ series, colors, ...props }: LineChartProps) {
  const theme = useTheme();
  const chart = theme.palette.chart;
  const seriesColors = useChartColors(series?.length ?? 0);

  // Set line curve and stroke width smoothly
  const modifiedSeries = series?.map((s) => ({
    ...s,
    curve: s.curve ?? "linear",
    highlightScope: s.highlightScope ?? { highlighted: "item", faded: "global" },
  }));

  return (
    <MuiLineChart
      series={modifiedSeries ?? []}
      colors={colors ?? seriesColors}
      grid={{ horizontal: true }}
      slots={{
        axisContent: CustomTooltip,
      }}
      slotProps={{
        legend: {
          itemMarkWidth: 12,
          itemMarkHeight: 12,
          padding: { bottom: 20 },
        },
      }}
      sx={{
        "& .MuiChartsAxis-line": { stroke: `${chart.axisFg} !important` },
        "& .MuiChartsAxis-tick": { stroke: `${chart.axisFg} !important` },
        "& .MuiChartsAxis-tickLabel": {
          fill: `${chart.labelFg} !important`,
          fontFamily: `${theme.typography.fontFamily} !important`,
          fontSize: `${fontSize.xxs}px !important`,
          lineHeight: lineHeight.xxs,
        },
        "& .MuiChartsAxis-label": {
          fill: `${chart.labelFgEmphasis} !important`,
          fontFamily: `${theme.typography.fontFamily} !important`,
          fontSize: `${typeof theme.typography.body1.fontSize === "number" ? theme.typography.body1.fontSize : 14}px !important`,
          fontWeight: theme.typography.fontWeightMedium,
        },
        "& .MuiChartsGrid-line": {
          stroke: chart.gridlineFg,
          strokeDasharray: "4 4",
        },
        "& .MuiChartsLegend-series text": {
          fill: `${chart.legendFg} !important`,
          fontFamily: `${theme.typography.fontFamily} !important`,
          fontSize: "14px !important",
        },
        "& .MuiChartsLegend-mark": {
          ry: 4,
          rx: 4,
          width: 12,
          height: 12,
        },
        "& .MuiChartsTooltip-root": {
          fontFamily: theme.typography.fontFamily,
          fontSize: typeof theme.typography.body1.fontSize === "number" ? theme.typography.body1.fontSize : 14,
        },
        "& .MuiChartsTooltip-table": {
          backgroundColor: chart.tooltipBg,
          color: chart.tooltipFg,
          borderRadius: theme.shape.borderRadius,
          boxShadow: theme.shadows[4],
        },
        "& .MuiLineElement-root": {
          strokeWidth: 3,
        },
        "& .MuiMarkElement-root": {
          display: "none",
        },
        ...((props.sx as object) ?? {}),
      }}
      {...props}
    />
  );
}
